import jwt from 'jsonwebtoken'
import _, { isEmpty } from 'lodash'
import Raven from 'raven-js'
import { APP_NAME, MANAGE_OTHER_USERS, TOKEN } from './permissions'
import settings from './settings'
import { serializeError } from './utils'

export const fetchJSON = (url, options = {}) =>
  new Promise((resolve, reject) => {
    return window.fetch(url, options)
      .then(response => {
        if (response.status === 204) {
          return response
        } else if (response.status >= 200 && response.status < 300) {
          return response.json()
        } else if (response.status === 401) {
          return getServerToken().then(() => fetchJSON(url, {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${window.sessionStorage[TOKEN]}`
            }
          }))
        }
        if (settings.isProduction) Raven.captureException(response)
        return reject(response)
      })
      .then(response => resolve(response))
      .catch(error => {
        if (settings.isProduction) Raven.captureException(serializeError(error))
        return reject(error)
      })
  })

const fetchBlob = (url, options = {}) =>
  new Promise((resolve, reject) => {
    return window.fetch(url, options)
      .then(response => {
        if (response.status >= 200 && response.status < 300) return response.blob()
        if (settings.isProduction) Raven.captureException(response)
        return reject(response)
      })
      .then(response => resolve(response))
      .catch(error => {
        if (settings.isProduction) Raven.captureException(serializeError(error))
        reject(error)
      })
  })

/* AWS Lambda Helpers */
export const getData = (endpoint, isPublication) => {
  const url = endpoint.match(/^(https?:)?\/\//i)
    ? endpoint
    : `${isPublication ? settings.API_PUBLICATION_ROUTE_AWS : settings.API_ROUTE_AWS}/${endpoint}`
  console.log(url)

  return fetchJSON(url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${window.sessionStorage[TOKEN]}`
    }
  })
}

export const getBlob = endpoint =>
  fetchBlob(endpoint, {
    method: 'GET',
    mode: 'cors'
  })

export const uploadData = (endpoint, data) =>
  window.fetch(endpoint, {
    method: 'PUT',
    headers: { 'Content-Type': data.type },
    body: data
  }).then(r => {
    if (!r.ok) return Promise.reject(r)
    return r
  })

export const putData = (endpoint, data, isPublication) =>
  fetchJSON(`${isPublication ? settings.API_PUBLICATION_ROUTE_AWS : settings.API_ROUTE_AWS}/${endpoint}`, {
    method: 'PUT',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${window.sessionStorage[TOKEN]}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify(data)
  })

export const postData = (endpoint, data, isPublication) =>
  fetchJSON(`${isPublication ? settings.API_PUBLICATION_ROUTE_AWS : settings.API_ROUTE_AWS}/${endpoint}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${window.sessionStorage[TOKEN]}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify(data)
  })

export const deleteData = (endpoint, data, isPublication) =>
  fetchJSON(`${isPublication ? settings.API_PUBLICATION_ROUTE_AWS : settings.API_ROUTE_AWS}/${endpoint}`, {
    method: 'DELETE',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${window.sessionStorage[TOKEN]}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify(data)
  })

/* IPD Authorization */
export const getLocalToken = () => window.sessionStorage[TOKEN]

export const getServerToken = () =>
  new Promise((resolve, reject) => {
    return window.fetch(`https://secure.ipdanalytics.com/Admin/Api/Login?appName=${APP_NAME}&scopes=AppHeader`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        return response.json()
      } else if (response.status === 401) {
        window.sessionStorage.removeItem(TOKEN)
        window.location.href = `https://secure.ipdanalytics.com/User/Login?returnURL=${encodeURIComponent(window.location || '')}`
        if (settings.isProduction) Raven.captureException(response)
        return reject(response)
      }
      return reject(response)
    })
    .then(response => {
      window.sessionStorage.setItem(TOKEN, response.token)
      return resolve(response)
    })
    .catch(error => {
      if (settings.isProduction) Raven.captureException(serializeError(error))
      reject(error)
    })
  })

export const checkIsAdmin = () => {
  const goHome = () => {
    if (window.location.pathname.includes('admin')) {
      window.location.pathname = '/'
    }
    return false
  }
  const token = window.sessionStorage[TOKEN]
  if (!token || isEmpty(token)) {
    return false
  }

  if (!checkHasClaim(MANAGE_OTHER_USERS)) {
    return goHome()
  }

  return true
}

export const checkHasClaim = claims => {
  if (!claims || isEmpty(claims)) return false
  if (!Array.isArray(claims)) claims = [claims]
  const token = window.sessionStorage[TOKEN]
  if (!token || isEmpty(token)) { return false }

  const decodedToken = jwt.decode(token)
  if (!decodedToken || isEmpty(decodedToken)) { return false }

  const userClaims = decodedToken['http://secure.ipdanalytics.com/claims/subscriberPermission']
  if (!userClaims || isEmpty(userClaims)) { return false }
  for (const claim of claims) {
    if (userClaims.includes(claim)) { return true }
  }

  return false
}

export const ajaxQueue = (batchSize, batchDelay) => {
  batchSize = typeof batchSize === 'undefined' ? 5 : batchSize
  batchDelay = typeof batchDelay === 'undefined' ? 100 : batchDelay

  const delay = ms => new Promise(resolve => setTimeout(resolve, ms))
  const tasks = []
  return {
    push: task => tasks.push(task),
    execute: async (progressCallback) => {
      let chunkIndex = 0
      const chunks = _.chunk(tasks, batchSize)
      for (const chunk of chunks) {
        if (chunkIndex) await delay(batchDelay)
        await Promise.all(chunk.map(t => t()))
        if (progressCallback) await progressCallback(chunkIndex, chunks.length, batchSize)
        chunkIndex++
      }
    }
  }
}
